import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, SpinLoading, Toast } from 'antd-mobile';
import { CheckCircleOutline, CloseCircleOutline } from 'antd-mobile-icons';
import { scanSpring2026QRCode } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './Spring2026QRCodePage.css';

export default function Spring2026QRCodePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, initialized, isAuthenticated } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null); // { success: true/false, message: string }
  const hasProcessedRef = useRef(false); // 防止重复处理

  useEffect(() => {
    // 防止重复处理
    if (hasProcessedRef.current) {
      return;
    }

    // 等待认证状态初始化
    if (!initialized) {
      return;
    }

    // 检查二维码参数
    const code = searchParams.get('code');
    if (!code) {
      hasProcessedRef.current = true;
      Toast.show({
        icon: 'fail',
        content: 'Invalid QR code link',
      });
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);
      return;
    }

    // 如果未登录，保存二维码并跳转到登录页
    if (!isAuthenticated || !user?.id) {
      hasProcessedRef.current = true;
      // 保存二维码到 localStorage，登录后可以继续扫描
      localStorage.setItem('pending-spring2026-code', code);
      Toast.show({
        icon: 'fail',
        content: 'Please sign in first',
      });
      setTimeout(() => {
        // 跳转到登录页，登录成功后会自动回到扫描流程
        navigate(`/login?redirect=/spring2026?code=${encodeURIComponent(code)}`, { replace: true });
      }, 1500);
      return;
    }

    // 如果已经扫描过，不再重复扫描
    if (scanning || scanResult) {
      return;
    }

    // 标记为已处理，防止重复执行
    hasProcessedRef.current = true;

    // 自动执行扫描
    const handleScan = async () => {
      setScanning(true);
      try {
        await scanSpring2026QRCode(code, user.id);
        setScanResult({
          success: true,
          message: 'Scan successful. You have received an RM5 coupon.',
        });
        Toast.show({
          icon: 'success',
          content: 'Scan successful. You have received an RM5 coupon.',
        });

        // 清除待处理的二维码
        localStorage.removeItem('pending-spring2026-code');

        // 3秒后跳转到首页
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      } catch (error) {
        setScanResult({
          success: false,
          message: error.message || 'Scan failed. Please try again.',
        });
        Toast.show({
          icon: 'fail',
          content: error.message || 'Scan failed. Please try again.',
        });

        // 3秒后跳转到首页
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      } finally {
        setScanning(false);
      }
    };

    handleScan();
  }, [searchParams, initialized, isAuthenticated, user, navigate]);

  // 显示加载状态
  if (!initialized || scanning) {
    return (
      <div className="spring2026-qrcode-page">
        <div className="spring2026-qrcode-container">
          <SpinLoading color="primary" style={{ fontSize: '48px' }} />
          <div className="spring2026-qrcode-loading-text">Processing...</div>
        </div>
      </div>
    );
  }

  // 显示扫描结果
  if (scanResult) {
    return (
      <div className="spring2026-qrcode-page">
        <div className="spring2026-qrcode-container">
          <Card className="spring2026-qrcode-result-card">
            {scanResult.success ? (
              <>
                <CheckCircleOutline
                  style={{ fontSize: '64px', color: '#52c41a', marginBottom: '16px' }}
                />
                <div className="spring2026-qrcode-result-title">Scan successful</div>
                <div className="spring2026-qrcode-result-message">{scanResult.message}</div>
              </>
            ) : (
              <>
                <CloseCircleOutline
                  style={{ fontSize: '64px', color: '#ff4d4f', marginBottom: '16px' }}
                />
                <div className="spring2026-qrcode-result-title">Scan failed</div>
                <div className="spring2026-qrcode-result-message">{scanResult.message}</div>
              </>
            )}
            <Button
              block
              color="primary"
              onClick={() => navigate('/', { replace: true })}
              style={{ marginTop: '24px' }}
            >
              Back to Home
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  // 默认状态（不应该到达这里，但为了安全起见）
  return (
    <div className="spring2026-qrcode-page">
      <div className="spring2026-qrcode-container">
        <SpinLoading color="primary" style={{ fontSize: '48px' }} />
        <div className="spring2026-qrcode-loading-text">Processing...</div>
      </div>
    </div>
  );
}
